/**
 * Full player — TASK 8.3 (mock L730-768, view model `_fp()` L1143-1144).
 *
 * MOVED here from `modules/app/overlays.js`, where TASK 7.2 parked it while it
 * was static markup. It is now a live view: it subscribes to nothing itself,
 * but exposes {@link FullPlayerHandle.update} so the shell can repaint it from
 * the store on every transport tick without rebuilding the DOM (rebuilding
 * would drop focus out of the seek bar mid-drag, and the overlay stack's focus
 * trap would then have nothing to hold).
 *
 * What TASK 8.3 adds to the extracted markup:
 *   * a real {@link SeekBar} with ~5 s keyboard steps (AC 6.2);
 *   * the §10.6 BANNER row — one distinct message and its own Retry per state,
 *     with the mock's own per-state colours (L1143);
 *   * SWIPE on the artwork (mock `fp.touchStart`/`fp.touchEnd`, ±40 px), which
 *     the TASK 7.2 extraction dropped, with the transport buttons as the
 *     equivalent for everyone who cannot swipe (AC 6.3);
 *   * live play/pause, shuffle, repeat and favourite state.
 *
 * @module
 */

import { TESTIDS, ordinalTestId } from '@streamymusic/shared';


import { controlTestId, linkTestId, toggleTestId } from '../../app/testids.js';
import { append, classes, el } from '../../components/primitives/dom.js';
import { Spinner } from '../../components/primitives/Skeleton.js';

import { NEXT_GLYPH, PREV_GLYPH } from './glyphs.js';
import { SWIPE_THRESHOLD_PX } from './MiniPlayer.js';
import { SeekBar } from './SeekBar.js';

/**
 * Per-state banner treatment, verbatim from the mock's `banners` map (L1143).
 * `retry` is decided by `lib/audio-engine.ts` and travels on the banner, so
 * the two cannot drift.
 */
export const BANNER_COLORS = Object.freeze({
  'buffering': 'var(--sm-color-yellow)',
  'error': 'var(--sm-color-pink)',
  'format-unsupported': 'var(--sm-color-pink)',
  'no-longer-available': 'var(--sm-color-yellow)',
  'rate-limited': 'var(--sm-color-pink)',
  'stream-limit': 'var(--sm-color-pink)',
  'egress-quota': 'var(--sm-color-pink)',
  'playback-halted': 'var(--sm-color-pink)',
});

/* ── Synced-lyrics panel (Spotify/Apple-Music-style active-line highlight) ── */

/** In-panel copy, one message per lyrics state. */
export const LYRICS_LOADING_MESSAGE = 'Loading lyrics…';
export const LYRICS_ERROR_MESSAGE = 'Couldn’t load lyrics.';
export const LYRICS_NONE_MESSAGE = 'No lyrics available';
export const LYRICS_INSTRUMENTAL_MESSAGE = '♪ Instrumental';
/** The plain (unsynced) state's subtle "these do not scroll with you" note. */
export const LYRICS_PLAIN_NOTE = 'Not time-synced';

/**
 * Highlight lead, in seconds — the active line flips this much BEFORE its cue
 * time, so the bright line is already the one about to be sung rather than the
 * one just finished. Matches the ~150 ms a listener reads a line ahead.
 */
export const LYRICS_LEAD_SEC = 0.15;

/**
 * The index of the ACTIVE synced line at `position` seconds: the LAST line
 * whose `time <= position`. Returns `-1` before the first cue (intro), and the
 * final index once `position` is past the last cue (outro stays lit).
 *
 * Pure + exported so the highlight rule is unit-testable without a DOM. Relies
 * on `lines` being sorted ascending by `time`, which `parseLrc` guarantees
 * (packages/shared/src/lyrics.ts) — so the scan can stop at the first cue in
 * the future.
 *
 * @param {ReadonlyArray<{ time: number }>} lines ascending by `time`
 * @param {number} position seconds (usually `model.position + LYRICS_LEAD_SEC`)
 * @returns {number} active index, or `-1` when before the first line
 */
export function activeLineIndex(lines, position) {
  let index = -1;
  for (let i = 0; i < lines.length; i += 1) {
    if (lines[i].time <= position) index = i;
    else break;
  }
  return index;
}

/**
 * @typedef {object} FullPlayerModel
 * @property {string} title
 * @property {string} artist "<artist> — <album>" (mock `fp.artist`)
 * @property {string} initials
 * @property {string} background CSS gradient for the artwork (mock `fp.bg`)
 * @property {string|undefined} artUrl real cover-art url (`/api/art/:albumId`)
 *   when the album has a real `covers/…` cover, else `undefined` — the gradient
 *   + initials then stand alone (Shell derives this via `albumArtUrl`).
 * @property {boolean} playing
 * @property {boolean} buffering
 * @property {boolean} shuffle
 * @property {'off'|'all'|'one'} repeat
 * @property {boolean} favorite
 * @property {number} position seconds
 * @property {number} duration seconds
 * @property {import('../../app/state-store.js').PlaybackBanner|null} banner
 * @property {string|null} trackId the current track's id — the key the lyrics
 *   panel fetches + caches by; `null` when nothing is playing. A CHANGE here is
 *   what makes the panel drop a stale response and load the new track's lyrics.
 */

/**
 * @typedef {object} FullPlayerHandlers
 * @property {() => void} onClose
 * @property {() => void} onPrevious
 * @property {() => void} onNext
 * @property {() => void} onToggle
 * @property {() => void} onShuffle
 * @property {() => void} onRepeat
 * @property {() => void} onFavorite
 * @property {() => void} onTrackActions
 * @property {(seconds: number) => void} onSeek
 * @property {() => void} onRetry
 * @property {(trackId: string) => Promise<import('@streamymusic/shared').LyricsResponse>} onFetchLyrics
 *   Resolves the lyrics for one track (the api-client's `getTrackLyrics`, which
 *   the composition root owns). Only ever called with a real id — the panel
 *   handles the nothing-playing case itself. It caches per trackId and drops a
 *   stale response under its own generation guard, so this stays a plain fetch.
 */

/**
 * @typedef {import('../../app/OverlayStack.js').OverlayDescriptor & {
 *   update: (model: FullPlayerModel) => void,
 * }} FullPlayerHandle
 */

/**
 * @param {{ model: FullPlayerModel, handlers: FullPlayerHandlers }} options
 * @returns {FullPlayerHandle}
 */
export function FullPlayer(options) {
  const { handlers } = options;
  let model = options.model;

  // mock L731
  const overlay = el('div', {
    className: 'sm-player-overlay',
    attrs: {
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': 'Now playing',
      'data-screen-label': 'Full player',
    },
  });
  const column = el('div', { className: 'sm-player-column' }); // mock L732

  /* ── header row (mock L733-737) ─────────────────────────────────────── */
  const close = el('button', {
    className: 'sm-btn sm-btn--icon sm-btn--icon-22',
    text: '⌄',
    testId: controlTestId('Close player'),
    attrs: { type: 'button', 'aria-label': 'Close player' },
    on: { click: () => handlers.onClose() },
  });
  /*
   * The LYRICS ⇄ ARTWORK toggle — grouped with the queue link as a "what am I
   * looking at" control. ONE stable `lyrics-toggle` testid across both states
   * (the accessible name + visible label flip, WCAG 2.5.3 Label-in-Name, but
   * the selector must not). `toggleLyrics` is a hoisted declaration below.
   */
  const lyricsToggle = el('button', {
    className: 'sm-player-lyrics-toggle',
    text: 'LYRICS',
    testId: toggleTestId('Lyrics'),
    attrs: { type: 'button', 'aria-label': 'Show lyrics', 'aria-pressed': 'false' },
    on: { click: () => toggleLyrics() },
  });
  append(
    column,
    append(
      el('div', { className: 'sm-flex-row-10' }),
      close,
      el('span', { className: 'sm-player-eyebrow', text: 'STEAMY PLAYER' }),
      lyricsToggle,
      el('a', {
        className: 'sm-player-queue-link',
        text: '☰',
        testId: linkTestId('Open queue'),
        attrs: { href: '#/queue', 'aria-label': 'Open queue' },
      }),
    ),
  );

  /* ── artwork + swipe (mock L738-743) ────────────────────────────────── */
  const artwork = el('div', { className: 'sm-cover sm-cover--player' });
  const initials = el('span', { className: 'sm-cover__initials', text: model.initials });
  const bufferScrim = append(
    el('span', { className: 'sm-buffer-scrim sm-buffer-scrim--player' }),
    Spinner({ size: 'player', label: 'Buffering' }),
  );
  append(artwork, initials);

  /**
   * The real cover-art layer over the gradient artwork — the SAME `.sm-cover__art-img`
   * layer the album tiles/hero use (`.sm-cover--player` is already a
   * `position:relative;overflow:hidden` container). Managed in place by
   * {@link syncArt}: none when `artUrl` is undefined, so an art-less track keeps
   * the gradient + initials — the intended fallback, not a regression.
   *
   * @type {HTMLImageElement|null}
   */
  let artImg = null;

  /** Adds / replaces / removes the cover image to match `model.artUrl`. */
  function syncArt() {
    const url = model.artUrl;
    if (url === undefined) {
      if (artImg !== null) {
        artImg.remove();
        artImg = null;
      }
      return;
    }
    if (artImg !== null) {
      if (artImg.getAttribute('src') === url) return; // unchanged — leave it
      artImg.remove();
      artImg = null;
    }
    const img = /** @type {HTMLImageElement} */ (
      el('img', {
        className: 'sm-cover__art-img',
        attrs: { src: url, alt: '', loading: 'lazy', decoding: 'async' },
        // A broken/404 cover removes itself, revealing the gradient + initials.
        on: {
          error: () => {
            img.remove();
            if (artImg === img) artImg = null;
          },
        },
      })
    );
    artImg = img;
    // Keep a buffering spinner (a later child) painted ON TOP of the cover.
    artwork.insertBefore(img, bufferScrim.parentNode === artwork ? bufferScrim : null);
  }

  let touchStartX = 0;
  const artHost = el('div', {
    className: 'sm-player-art',
    on: {
      // AC 6.3 — swipe left = next, right = previous/restart. The transport
      // buttons below do exactly the same two things, so the gesture is an
      // accelerator and never the only way to reach the behaviour.
      touchstart: (event) => {
        const touch = /** @type {TouchEvent} */ (event).touches[0];
        if (touch !== undefined) touchStartX = touch.clientX;
      },
      touchend: (event) => {
        const touch = /** @type {TouchEvent} */ (event).changedTouches[0];
        if (touch === undefined) return;
        const dx = touch.clientX - touchStartX;
        if (dx < -SWIPE_THRESHOLD_PX) handlers.onNext();
        else if (dx > SWIPE_THRESHOLD_PX) handlers.onPrevious();
      },
    },
  });
  append(artHost, artwork);
  append(column, artHost);

  /* ── synced-lyrics panel (Spotify/Apple-Music active-line highlight) ─────
   *
   * Occupies the SAME slot as the artwork — the toggle swaps one for the other
   * (`hidden`), it never rebuilds the dialog, and it scrolls INTERNALLY so the
   * fixed header/nav and the page body never move.
   *
   * DATA LIFECYCLE (closure state, keyed by trackId): the panel is built once
   * and `paint()`ed ~4x/sec, so lyrics live in these closures, not the model.
   * A fetch runs only while the panel is OPEN — on first open, and whenever
   * `model.trackId` changes under it — under a per-fetch generation guard, so a
   * response that lands after the track already moved on is dropped, never
   * painted (the HomeView `loadSection` pattern). Every trackId's answer is
   * cached, so re-opening a track (or toggling off/on) never re-fetches.
   */
  const lyricsHost = el('div', {
    className: 'sm-lyrics',
    attrs: { hidden: true, role: 'group', 'aria-label': 'Lyrics', tabindex: '0' },
  });
  append(column, lyricsHost);

  /** Whether the lyrics panel (not the artwork) is currently showing. */
  let lyricsVisible = false;
  /** The trackId the panel holds/loads content for; `undefined` = never loaded. */
  let loadedTrackId;
  /** Bumped per fetch; a settle whose generation is stale writes nothing. */
  let lyricsGeneration = 0;
  /** trackId -> LyricsResponse — re-opening a track never re-fetches. */
  const lyricsCache = new Map();
  /**
   * The rendered synced rows `[{ time, node }]`, ascending by `time`, or `null`
   * for every non-synced state (only 'synced' has a moving highlight).
   * @type {{ time: number, node: HTMLElement }[] | null}
   */
  let syncedLines = null;
  /** Index of the currently-lit synced line; -1 = none. Guards the 4 Hz paint. */
  let activeLineIdx = -1;

  /** A centered one-line state (loading / instrumental / none / error). */
  function renderLyricsState(message, variant) {
    syncedLines = null;
    activeLineIdx = -1;
    lyricsHost.replaceChildren(
      el('div', {
        className: classes('sm-lyrics__state', `sm-lyrics__state--${variant}`),
        text: message,
      }),
    );
  }

  /** Plain (unsynced) text — line breaks preserved, a subtle note, no highlight. */
  function renderPlainLyrics(text) {
    syncedLines = null;
    activeLineIdx = -1;
    lyricsHost.replaceChildren(
      append(
        el('div', { className: 'sm-lyrics__plain' }),
        el('p', { className: 'sm-lyrics__note', text: LYRICS_PLAIN_NOTE }),
        el('div', { className: 'sm-lyrics__plain-text', text }),
      ),
    );
  }

  /** Synced cues — one element per line; a tap seeks to that line's time. */
  function renderSyncedLyrics(lines) {
    if (lines.length === 0) {
      renderLyricsState(LYRICS_NONE_MESSAGE, 'none');
      return;
    }
    const list = el('div', { className: 'sm-lyrics__lines' });
    /** @type {{ time: number, node: HTMLElement }[]} */
    const rows = [];
    for (const line of lines) {
      // A blank-text timed cue is an interlude/gap — a non-interactive marker,
      // not a seek target with an empty accessible name.
      const node =
        line.text === ''
          ? el('div', {
              className: classes('sm-lyrics__line', 'sm-lyrics__line--gap'),
              attrs: { 'aria-hidden': 'true' },
            })
          : el('button', {
              className: 'sm-lyrics__line',
              text: line.text,
              attrs: { type: 'button' },
              // Nice-to-have: jump the transport to this line (the seek handler
              // already exists — no new backend). Muted lines are otherwise
              // read-only.
              on: { click: () => handlers.onSeek(line.time) },
            });
      rows.push({ time: line.time, node });
      append(list, node);
    }
    lyricsHost.replaceChildren(list);
    syncedLines = rows;
    activeLineIdx = -1;
    updateActiveLine();
  }

  /** @param {import('@streamymusic/shared').LyricsResponse} res */
  function renderLyrics(res) {
    switch (res.status) {
      case 'synced':
        renderSyncedLyrics(res.lines ?? []);
        break;
      case 'plain':
        renderPlainLyrics(res.plain ?? '');
        break;
      case 'instrumental':
        renderLyricsState(LYRICS_INSTRUMENTAL_MESSAGE, 'instrumental');
        break;
      default:
        renderLyricsState(LYRICS_NONE_MESSAGE, 'none');
    }
  }

  /** Loads (or serves from cache) the lyrics for `trackId`; guards staleness. */
  function loadLyrics(trackId) {
    loadedTrackId = trackId;
    if (trackId === null || trackId === undefined) {
      renderLyricsState(LYRICS_NONE_MESSAGE, 'none');
      return;
    }
    const cached = lyricsCache.get(trackId);
    if (cached !== undefined) {
      renderLyrics(cached);
      return;
    }
    const own = (lyricsGeneration += 1);
    renderLyricsState(LYRICS_LOADING_MESSAGE, 'loading');
    Promise.resolve(handlers.onFetchLyrics(trackId)).then(
      (res) => {
        lyricsCache.set(trackId, res); // cache even if stale — a later re-open wins
        if (own !== lyricsGeneration) return; // the track changed mid-flight
        renderLyrics(res);
      },
      () => {
        // A transport failure is an inline panel state — it never blanks the
        // player, never auto-retries.
        if (own !== lyricsGeneration) return;
        renderLyricsState(LYRICS_ERROR_MESSAGE, 'error');
      },
    );
  }

  /** Re-lights the active synced line — DOM only when the index actually moves. */
  function updateActiveLine() {
    if (syncedLines === null) return;
    const idx = activeLineIndex(syncedLines, model.position + LYRICS_LEAD_SEC);
    if (idx === activeLineIdx) return;
    if (activeLineIdx >= 0 && activeLineIdx < syncedLines.length) {
      syncedLines[activeLineIdx].node.classList.remove('is-active');
    }
    activeLineIdx = idx;
    if (idx < 0) return;
    const node = syncedLines[idx].node;
    node.classList.add('is-active');
    scrollLineToCenter(node);
  }

  /**
   * Smoothly centers the active line. `scrollTo` with no `behavior` inherits
   * the container's CSS `scroll-behavior` (smooth, auto under reduced-motion),
   * and the `scrollTop` fallback keeps jsdom / old webviews from throwing.
   */
  function scrollLineToCenter(node) {
    const top = Math.max(0, node.offsetTop - (lyricsHost.clientHeight - node.offsetHeight) / 2);
    if (typeof lyricsHost.scrollTo === 'function') lyricsHost.scrollTo({ top });
    else lyricsHost.scrollTop = top;
  }

  /** Per-tick lyrics work: (re)load on a track change, else move the highlight. */
  function syncLyrics() {
    if (!lyricsVisible) return;
    if (model.trackId !== loadedTrackId) {
      loadLyrics(model.trackId);
      return;
    }
    updateActiveLine();
  }

  /** Swaps artwork ⇄ lyrics and relabels the toggle (visible text + a11y name). */
  function applyLyricsVisibility() {
    artHost.hidden = lyricsVisible;
    lyricsHost.hidden = !lyricsVisible;
    lyricsToggle.setAttribute('aria-pressed', String(lyricsVisible));
    lyricsToggle.setAttribute('aria-label', lyricsVisible ? 'Show artwork' : 'Show lyrics');
    lyricsToggle.textContent = lyricsVisible ? 'ARTWORK' : 'LYRICS';
  }

  function toggleLyrics() {
    lyricsVisible = !lyricsVisible;
    applyLyricsVisibility();
    syncLyrics(); // load immediately on open, don't wait for the next store tick
  }

  /* ── metadata (mock L744-747) ───────────────────────────────────────── */
  const titleLine = el('div', { className: 'sm-player-meta__title', text: model.title });
  const artistLine = el('div', { className: 'sm-player-meta__artist', text: model.artist });
  append(column, append(el('div', { className: 'sm-player-meta' }), titleLine, artistLine));

  /* ── banner (mock L749 / `_fp` banners map) ─────────────────────────── */
  /*
   * Built ONCE and shown/hidden, not rebuilt per paint. `paint()` runs on every
   * store tick (~4 Hz while audio plays), and rebuilding meant the Retry button
   * the user is reaching for was a brand-new node on every dispatch: a moving
   * target for a pointer, and focus dropped if it had any.
   */
  const bannerHost = el('div');
  const bannerText = el('div', { className: 'sm-banner__text' });
  const bannerRetry = el('button', {
    className: 'sm-btn sm-btn--retry sm-btn--xs',
    text: 'Retry',
    testId: TESTIDS.RETRY_BUTTON,
    attrs: { type: 'button' },
    on: { click: () => handlers.onRetry() },
  });
  const bannerBox = append(
    el('div', { className: 'sm-banner sm-banner--player', attrs: { role: 'status' } }),
    bannerText,
  );
  append(column, bannerHost);

  /* ── seek (mock L751-754) ───────────────────────────────────────────── */
  const seek = SeekBar({
    position: model.position,
    duration: model.duration,
    onSeek: (seconds) => handlers.onSeek(seconds),
  });
  append(column, seek.element);

  /* ── transport (mock L755-761) ──────────────────────────────────────────
   *
   * ORDINAL TESTIDS, and which surface gets the bare one.
   *
   * While `/player` is open, the mini-player dock stays mounted underneath, so
   * `pause-button`, `next-track-button` and `previous-track-button` each
   * resolve to TWO nodes. Not an accessibility defect — the background is
   * inerted by the overlay stack — but it makes every §3 selector ambiguous,
   * and §3's own convention for repeated same-label controls is
   * `ordinalTestId`: first instance bare, subsequent ones `-2`, `-3`.
   *
   * The DOCK keeps the bare id and the full player takes `-2`, which is the
   * DOM order (the dock lives in the shell background, the overlay is appended
   * after it). The alternative — giving the foreground the bare id — was
   * rejected because it makes the id's MEANING route-dependent: `pause-button`
   * would be the dock on `/` and the full player on `/player`, so a selector
   * written once would silently target two different controls. This way the
   * bare id is the dock on every route, and `-2` is unambiguously the full
   * player, which only exists on `/player`.
   */
  const shuffleButton = el('button', {
    className: 'sm-btn sm-btn--fp-mode',
    text: '⤨',
    testId: controlTestId('Shuffle'),
    attrs: { type: 'button', 'aria-label': 'Shuffle' },
    on: { click: () => handlers.onShuffle() },
  });
  const previousButton = el('button', {
    className: 'sm-btn sm-btn--fp-skip',
    text: PREV_GLYPH,
    testId: ordinalTestId(TESTIDS.PREVIOUS_TRACK_BUTTON, 2),
    attrs: { type: 'button', 'aria-label': 'Previous track' },
    on: { click: () => handlers.onPrevious() },
  });
  const toggleButton = el('button', {
    className: 'sm-btn sm-btn--fp-toggle',
    testId: ordinalTestId(TESTIDS.PAUSE_BUTTON, 2),
    attrs: { type: 'button' },
    on: { click: () => handlers.onToggle() },
  });
  const nextButton = el('button', {
    className: 'sm-btn sm-btn--fp-skip',
    text: NEXT_GLYPH,
    testId: ordinalTestId(TESTIDS.NEXT_TRACK_BUTTON, 2),
    attrs: { type: 'button', 'aria-label': 'Next track' },
    on: { click: () => handlers.onNext() },
  });
  const repeatButton = el('button', {
    className: 'sm-btn sm-btn--fp-mode',
    testId: controlTestId('Repeat'),
    attrs: { type: 'button' },
    on: { click: () => handlers.onRepeat() },
  });
  append(
    column,
    append(
      el('div', { className: 'sm-player-transport' }),
      shuffleButton,
      previousButton,
      toggleButton,
      nextButton,
      repeatButton,
    ),
  );

  /* ── secondary row (mock L762-765) ──────────────────────────────────── */
  const favoriteLabel = el('span', { className: 'sm-btn__pill-label', text: 'Favorite' });
  const favorite = el('button', {
    className: 'sm-btn sm-btn--player-pill',
    attrs: { type: 'button' },
    on: { click: () => handlers.onFavorite() },
  });
  append(favorite, document.createTextNode('♥ '), favoriteLabel);

  const trackActions = el('button', {
    className: 'sm-btn sm-btn--player-pill',
    testId: controlTestId('Track actions'),
    attrs: { type: 'button', 'aria-label': 'Track actions' },
    on: { click: () => handlers.onTrackActions() },
  });
  append(
    trackActions,
    document.createTextNode('⋯ '),
    el('span', { className: 'sm-btn__pill-label', text: 'More' }),
  );

  append(column, append(el('div', { className: 'sm-player-secondary' }), favorite, trackActions));

  // mock L766
  append(
    column,
    el('p', {
      className: 'sm-footnote sm-footnote--player',
      text: 'Swipe the artwork ← next · → previous/restart. Buttons do the same.',
    }),
  );

  overlay.append(column);

  /* ── painting ───────────────────────────────────────────────────────── */

  function paintBanner() {
    // The mock renders the buffering state as a banner too; the engine keeps
    // `buffering` separate from a FAILURE banner, so the two are merged here.
    // Feature request #9: buffering is signalled by the spinner over the cover
    // art alone. It no longer inflates a full-width banner card that reflowed
    // the transport on every single track change (the "buffering card is
    // bothering" report). The banner is now FAILURES-only.
    const banner = model.banner;

    if (banner === null) {
      bannerBox.remove();
      return;
    }

    const color = BANNER_COLORS[banner.kind] ?? 'var(--sm-color-yellow)';
    bannerBox.style.setProperty('border-color', color);
    bannerText.textContent = banner.message;
    bannerText.style.setProperty('color', color);

    if (banner.retry) {
      if (bannerRetry.parentNode === null) bannerBox.append(bannerRetry);
      /*
       * A 429 names a wait, and `lib/audio-engine.ts` now ENFORCES it: a press
       * inside the window issues no request at all. Rendering the control as
       * enabled would make that look like the app was broken rather than
       * throttled, so it is disabled — and left in place rather than removed,
       * so the banner does not change height and the reason stays legible in
       * the message beside it. `aria-disabled` mirrors `disabled` for the
       * assistive-tech tree.
       */
      const refused = banner.retryDisabled === true;
      bannerRetry.disabled = refused;
      bannerRetry.setAttribute('aria-disabled', String(refused));
      bannerRetry.setAttribute(
        'aria-label',
        refused && typeof banner.retryAfterSec === 'number'
          ? `Retry — available in about ${String(banner.retryAfterSec)} seconds`
          : 'Retry',
      );
    } else {
      bannerRetry.remove();
    }

    if (bannerBox.parentNode === null) bannerHost.append(bannerBox);
  }

  function paint() {
    artwork.style.setProperty('background', model.background);
    initials.textContent = model.initials;
    syncArt();
    if (model.buffering) {
      if (bufferScrim.parentNode === null) artwork.append(bufferScrim);
    } else {
      bufferScrim.remove();
    }

    titleLine.textContent = model.title;
    artistLine.textContent = model.artist;

    seek.update({ position: model.position, duration: model.duration });

    const toggleLabel = model.playing ? 'Pause' : 'Play';
    toggleButton.textContent = model.playing ? '‖' : '▶';
    toggleButton.setAttribute('aria-label', toggleLabel);

    shuffleButton.setAttribute('aria-pressed', String(model.shuffle));
    shuffleButton.style.setProperty(
      'color',
      model.shuffle ? 'var(--sm-color-teal)' : 'var(--sm-color-text-dim-2)',
    );

    repeatButton.textContent = model.repeat === 'one' ? '↻1' : '↻';
    repeatButton.setAttribute('aria-label', `Repeat: ${model.repeat}`);
    repeatButton.style.setProperty(
      'color',
      model.repeat === 'off' ? 'var(--sm-color-text-dim-2)' : 'var(--sm-color-teal)',
    );

    /*
     * WCAG 2.5.3 Label in Name — the VISIBLE text and the accessible name must
     * not disagree, or a speech-input user saying "Favorite" cannot activate a
     * control whose accessible name is "Unfavorite …". The visible label used
     * to be a static "Favorite" while only `aria-label` flipped.
     * `views/album/AlbumDetail.js` already swaps both for track rows; this is
     * the same treatment on the player.
     */
    const favoriteVerb = model.favorite ? 'Unfavorite' : 'Favorite';
    favoriteLabel.textContent = favoriteVerb;
    favorite.setAttribute('aria-label', `${favoriteVerb} ${model.title}`);
    favorite.setAttribute('aria-pressed', String(model.favorite));
    favorite.dataset.testid = controlTestId(model.favorite ? 'Unfavorite' : 'Favorite');
    favorite.style.setProperty(
      'color',
      model.favorite ? 'var(--sm-color-pink)' : 'var(--sm-color-text-muted)',
    );

    paintBanner();
    syncLyrics();
  }

  paint();

  return {
    kind: 'player',
    element: overlay,
    dialog: overlay,
    initialFocus: close,
    update(next) {
      model = next;
      paint();
    },
  };
}
